import type { Dictionary } from 'lodash';
import { CanvasGridPosition } from '../helpers/gridPositioningUtils';
import { SweepCanvasReactFlowEdgeDataType } from '../edges/SweepCanvasReactFlowEdgeDataType';
import { LogoDevBrand } from '../../../apis/facades/useLogoDevApiFacade';

export enum NodeEntityTypes {
  Step = 'Step',
  StepPill = 'StepPill',
  Group = 'Group',
  GroupPill = 'GroupPill',
  GroupLabel = 'GroupLabel',
  GhostStep = 'GhostStep',
  GhostStepButton = 'GhostStepButton',
  GroupEditButton = 'GroupEditButton',
  GroupOverlay = 'GroupOverlay',
  Standalone = 'Standalone',
  StandaloneMore = 'StandaloneMore',
  GroupMore = 'GroupMore',
  SectionDelete = 'SectionDelete',
}

interface NodeEntity {
  type: NodeEntityTypes;
}

export interface StepEntityType extends NodeEntity {
  type: NodeEntityTypes.Step;
}
export interface StepPillEntityType extends NodeEntity {
  type: NodeEntityTypes.StepPill;
  canvasPillType: CanvasPillTypes;
}

export interface GroupPillEntityType extends NodeEntity {
  type: NodeEntityTypes.GroupPill;
  canvasPillType: CanvasPillTypes;
}

export interface GroupLabelEntityType extends NodeEntity {
  type: NodeEntityTypes.GroupLabel;
}
export interface GhostStepEntityType extends NodeEntity {
  type: NodeEntityTypes.GhostStep;
}

export interface GhostStepButtonEntityType extends NodeEntity {
  type: NodeEntityTypes.GhostStepButton;
}

export interface GroupEntityType extends NodeEntity {
  type: NodeEntityTypes.Group;
}

export interface GroupEditButtonEntityType extends NodeEntity {
  type: NodeEntityTypes.GroupEditButton;
}

export interface GroupOverlayEntityType extends NodeEntity {
  type: NodeEntityTypes.GroupOverlay;
}

export interface StandaloneEntityType extends NodeEntity {
  type: NodeEntityTypes.Standalone;
}

export interface StandaloneMoreEntityType extends NodeEntity {
  type: NodeEntityTypes.StandaloneMore;
}

export interface GroupMoreEntityType extends NodeEntity {
  type: NodeEntityTypes.GroupMore;
}

export interface SectionDeleteEntityType extends NodeEntity {
  type: NodeEntityTypes.SectionDelete;
}

export type SweepCanvasEntities =
  | StepEntityType
  | StepPillEntityType
  | GroupPillEntityType
  | GroupLabelEntityType
  | GhostStepEntityType
  | GhostStepButtonEntityType
  | GroupEntityType
  | GroupEditButtonEntityType
  | GroupOverlayEntityType
  | StandaloneEntityType
  | StandaloneMoreEntityType
  | GroupMoreEntityType
  | SectionDeleteEntityType;

export interface OnNodeClickProps {
  id: string;
  parentId?: string;
  entity: SweepCanvasEntities;
  event: React.MouseEvent | React.TouchEvent;
  isLongClick?: boolean;
}

export enum CanvasElementType {
  REGULAR = 'REGULAR',
  EDITABLE = 'EDITABLE',
  NURTURING_BUCKET = 'NURTURING_BUCKET',
  DROP_ZONE_NODE = 'DROP_ZONE_NODE',
  GROUP = 'GROUP',
  GROUP_LABEL = 'GROUP_LABEL',
  GROUP_OVERLAY = 'GROUP_OVERLAY',
  GHOST_NODE = 'GHOST_NODE',
  STANDALONE_NODE = 'STANDALONE_NODE',
  SECTION = 'SECTION',
  STICKY_NOTE = 'STICKY_NOTE',
  TEXT_NODE = 'TEXT_NODE',
}

export enum CanvasEdgeConnectionType {
  Bezier = 'Bezier',
  HorizontalDetour = 'HorizontalDetour',
  VerticalDetour = 'VerticalDetour',
}

export enum CanvasPillTypes {
  automation = 'automation',
  playbookAlert = 'playbookAlert',
  assignmentRule = 'assignmentRule',
  scheduledAssignment = 'scheduledAssignment',
  validationRule = 'validationRule',
  sfdcAutomation = 'sfdcAutomation',
  apexTrigger = 'apexTrigger',
  groupSfdcAutomation = 'groupSfdcAutomation',
  groupValidationRule = 'groupValidationRule',
  groupApex = 'groupApex',
  groupAutomation = 'groupAutomation',
  groupDeduplication = 'groupDeduplication',
  groupMatching = 'groupMatching',
  groupAssignmentRule = 'groupAssignmentRule',
  groupPlaybookAlert = 'groupPlaybookAlert',
  groupAggregated = 'groupAggregated',
  groupScheduledAssignment = 'groupScheduledAssignment',
  groupHubspotEnrollment = 'groupHubspotEnrollment',
}

export interface CanvasPill {
  type: CanvasPillTypes;
  amount: number;
  highlight?: boolean;
}

interface CanvasNodeBase {
  nodeType: 'node' | 'group' | 'standalone' | 'section';
  id: string;
  name: string;
  position: CanvasGridPosition;
}

export interface CanvasNode extends CanvasNodeBase {
  nodeType: 'node';
  parentId?: string;
  type: CanvasElementType;
  originStepName?: string;
  probability?: number;
  forecastCategoryLabel?: string;
  hideNbButton?: boolean;
  showSparkleIcon?: boolean;
  allowNodeSwap?: boolean;
  objectType: ObjectTypeValues;
  pills?: CanvasPill[];
}
export interface CanvasGroup<T = any> extends CanvasNodeBase {
  nodeType: 'group';
  objectLabelLogoDevBrand?: LogoDevBrand;
  description?: string;
  objectLabel: string;
  metadata?: T;
  plugins?: PluginTypes[];
  showEditButton?: boolean;
  startingIcon?: 'hubspot';
  topTag?: string;
  objectType: ObjectTypeValues;
  pills?: CanvasPill[];
  status?: string;
  moreButton?: {
    display: true;
    isPressed?: boolean;
  };
  sectionId?: string;
}

export interface CanvasStandaloneNode extends CanvasNodeBase {
  nodeType: 'standalone';
  name: string;
  type: string;
  icon?: React.ReactNode;
  topTag?: string;
  color?: string;
  sectionId?: string;
}

export interface CanvasSection {
  nodeType: 'section';
  id: string;
  name: string;
  color: string;
  width: number;
  height: number;
  xyPosition: { x: number; y: number };
}

export interface CanvasStickyNote {
  nodeType: 'stickyNote';
  id: string;
  color: string;
  richText: string;
  width: number;
  height: number;
  xyPosition: { x: number; y: number };
  sectionId?: string;
  textScale: number; // Float
}

export interface CanvasTextNode {
  nodeType: 'textNode';
  id: string;
  richText: string;
  width: number;
  height: number;
  xyPosition: { x: number; y: number };
  sectionId?: string;
  textScale: number; // Float
}

export interface CanvasEdgeData {
  type: SweepCanvasReactFlowEdgeDataType;
  label?: string;
  connectionType?: CanvasEdgeConnectionType;
  connectionIdx?: number;
  groupConnection?: boolean;
}

export interface CanvasEdge {
  id: string;
  source: string;
  target: string;
  data: CanvasEdgeData;
}

export type CanvasEdgeTransformations = Dictionary<CanvasEdge>;
export type CanvasNodeTransformations = Dictionary<CanvasNode>;
export type CanvasGroupTransformations = Dictionary<CanvasGroup>;

export enum CanvasMode {
  DEFAULT = 'DEFAULT',
  PREVIEW1 = 'PREVIEW1', //basic step view structure (eg. no funnel/step name displayed)
  PREVIEW2 = 'PREVIEW2', //detailed step information (eg. with step name displayed) with zoom controls
  PREVIEW3 = 'PREVIEW3', //detailed step information (eg. with step name displayed) without zoom controls
  FIT_NODES_PREVIEW = 'FIT_NODES_PREVIEW',
}

export const DEFAULT_CANVAS_ZOOM = 1;
export const DEFAULT_CANVAS_MIN_ZOOM = 0.1;
export const DEFAULT_PREVIEW_MIN_ZOOM = 0.01;

export enum ContextZoomType {
  DEFAULT = 'DEFAULT',
  LEVEL1 = 'LEVEL1',
  LEVEL1_5 = 'LEVEL1_5', // no actions on hover
  LEVEL2 = 'LEVEL2',
}
